import type { ComponentProps } from 'react';
import React, { useState } from 'react';
import styled from '@emotion/styled';
import type {
  FlexDirectionProps,
  FlexProps,
  MaxWidthProps,
  SpaceProps,
  WidthProps,
} from 'styled-system';
import { flex, flexDirection, maxWidth, space, width } from 'styled-system';

import { useLocalization } from '../../providers/LocaleProvider';
import type { ListStyleProps } from '../../theme/system-utilities';
import { listStyle } from '../../theme/system-utilities';
import { Button } from '../Button';
import { Flex } from '../Flex';
import type { LinkStylesProps } from '../Link';
import { linkStyles } from '../Link';
import { HeadlineH8, LinkRegularLR2 } from '../Typography';
import RecentSearchesListItem from './RecentSearchesListItem';
import type { RecentSearches, SearchSuggestionsItemProps, SuggestionGetItemProps } from './types';
import { mapRecentSearchToSuggestionItem } from './utils/search';
import { trackEditRecentSearches, trackRemoveAllRecentSearches } from './utils/tracking';

interface RecentSearchesListProps {
  recentSearches: RecentSearches;
  listItems: string[];
  highlightedIndex: number;
  getItemProps: SuggestionGetItemProps<SearchSuggestionsItemProps>;
}

const Container = styled.div<SpaceProps & WidthProps & MaxWidthProps>`
  ${space};
  ${width};
  ${maxWidth};
`;

const containerStyles = {
  p: [null, null, 'sp_24'],
  width: 1,
  maxWidth: [null, null, null, '100%'],
};

const ListContainer = styled.ul<
  ListStyleProps & FlexProps & FlexDirectionProps & WidthProps & SpaceProps
>`
  ${listStyle};
  ${flex};
  ${flexDirection};
  ${width};
  ${space};
`;

const listContainerStyles: ComponentProps<typeof ListContainer> = {
  listStyle: 'none',
  flex: '1',
  flexDirection: 'column',
  width: 1,
  p: 'sp_0',
  m: 'sp_0',
};

const EditButton = styled(Button)<LinkStylesProps>`
  ${linkStyles};
`;

const editButtonStyles: ComponentProps<typeof EditButton> = {
  kind: 'bare',
  color: 'sys.neutral.text.strong',
  textDecoration: 'underline',
  styled: true,
};

const titleContainerStyles = {
  justifyContent: 'space-between',
  px: 'sp_8',
  py: 'sp_12',
};

const RecentSearchesList = ({
  recentSearches,
  highlightedIndex,
  getItemProps,
  listItems,
}: RecentSearchesListProps) => {
  const { t } = useLocalization();
  const [isEditable, setIsEditable] = useState(false);
  const suggestionItems = recentSearches.list.map(mapRecentSearchToSuggestionItem);

  const editLabel = t('h24_search_edit_recent_searches');
  const editClickHandler = () => {
    setIsEditable(true);
    trackEditRecentSearches(editLabel);
  };

  const deleteAllLabel = t('h24_search_delete_all');
  const deleteAllClickHandler = () => {
    recentSearches.handlers.removeAll();
    trackRemoveAllRecentSearches({
      label: deleteAllLabel,
      items: suggestionItems.map(({ name }) => name),
      length: suggestionItems.length,
    });
  };

  return (
    <Container data-testid="recent-searches" {...containerStyles}>
      <Flex {...titleContainerStyles}>
        <HeadlineH8 as="h2">{t('h24_search_recent_searches')}</HeadlineH8>
        <EditButton
          onClick={isEditable ? deleteAllClickHandler : editClickHandler}
          {...editButtonStyles}
        >
          <LinkRegularLR2 as="span" color="inherit">
            {isEditable ? deleteAllLabel : editLabel}
          </LinkRegularLR2>
        </EditButton>
      </Flex>
      <ListContainer {...listContainerStyles}>
        {suggestionItems.slice(0, 10).map((item, index) => (
          <RecentSearchesListItem
            key={item.name}
            {...getItemProps({
              item,
              // Downshift "swallows" item from the props,
              // we have to pass it with different key
              suggestionsItem: item,
              highlighted: highlightedIndex === index,
              listIndex: index,
              listItems,
              isEditable,
              onRemove: () => recentSearches.handlers.remove(index),
            })}
          />
        ))}
      </ListContainer>
    </Container>
  );
};

export default RecentSearchesList;
